
/**
 * Utility to parse OpenAPI specification and convert it to our internal endpoint format
 */

import { ApiEndpoint } from "@/hooks/useApiProviders";

interface OpenApiPathInfo {
  summary?: string;
  description?: string;
  parameters?: any[];
  requestBody?: any;
  responses?: Record<string, any>;
  [key: string]: any;
}

interface OpenApiPath {
  get?: OpenApiPathInfo;
  post?: OpenApiPathInfo;
  put?: OpenApiPathInfo;
  delete?: OpenApiPathInfo;
  patch?: OpenApiPathInfo;
  [key: string]: OpenApiPathInfo | undefined;
}

interface OpenApiSpecification {
  paths: Record<string, OpenApiPath>;
  [key: string]: any;
}

/**
 * Parse an OpenAPI specification to extract endpoints
 */
export function parseOpenApiSpec(
  spec: OpenApiSpecification, 
  providerId: string
): Omit<ApiEndpoint, "id">[] {
  const endpoints: Omit<ApiEndpoint, "id">[] = [];
  
  // Process each path in the specification
  for (const [path, methods] of Object.entries(spec.paths)) {
    // Process each HTTP method for the path
    for (const [method, info] of Object.entries(methods)) {
      if (!info || !['get', 'post', 'put', 'delete', 'patch'].includes(method)) {
        continue;
      }
      
      const name = info.summary || `${method.toUpperCase()} ${path}`;
      
      const endpoint: Omit<ApiEndpoint, "id"> = {
        provider_id: providerId,
        name,
        path,
        method: method.toUpperCase(),
        description: info.description || '',
        is_active: true
      };
      
      // Add request schema if available
      if (info.requestBody?.content?.['application/json']?.schema) {
        endpoint.request_schema = info.requestBody.content['application/json'].schema;
      }
      
      // Add response schema if available
      if (info.responses?.['200']?.content?.['application/json']?.schema) {
        endpoint.response_schema = info.responses['200'].content['application/json'].schema;
      }
      
      endpoints.push(endpoint);
    }
  }
  
  return endpoints;
}

/**
 * Import OpenAPI endpoints for a provider
 */
export async function importOpenApiEndpoints(
  spec: OpenApiSpecification, 
  providerId: string,
  createEndpoint: (endpoint: Omit<ApiEndpoint, "id">) => Promise<any>
): Promise<number> {
  const endpoints = parseOpenApiSpec(spec, providerId);
  
  let importedCount = 0;
  for (const endpoint of endpoints) {
    try {
      await createEndpoint(endpoint);
      importedCount++;
    } catch (error) {
      console.error(`Failed to import endpoint ${endpoint.name}:`, error);
    }
  }
  
  return importedCount;
}
